/**
 * Preisstufen je Basar (Endverbraucherpreise, brutto inklusive 19 % USt, Entscheidung E2, Antwort des
 * Auftraggebers zu Frage 2/3/5 in docs/00-PLAN.md Abschnitt 7). Beträge in ganzen Cent. Später aus der
 * Konfiguration (`price_plans`) lesbar; bestehende Basare behalten den Preis der Freischaltung (er wird
 * in `event_licenses` gespeichert, spätere Preisänderungen wirken nicht rückwirkend).
 *
 * Stand Oktober 2026: Buchbar sind nur noch „Kostenlos“ (bis 100 Teile) und „Unlimited“ (200 € brutto, ohne
 * Obergrenze). „Standard“ und „Groß“ sind Altwerte: Bestehende Lizenzen mit diesen Stufen bleiben gültig und
 * lesbar, neue Buchungen lassen sich damit nicht mehr auslösen (`BOOKABLE_LICENSE_TIERS`).
 * Zahlung: erst nach dem Basar fällig (siehe `paymentDueNote`).
 */
export const LICENSE_TIERS = ['free', 'standard', 'large', 'unlimited'] as const;
export type LicenseTier = (typeof LICENSE_TIERS)[number];

export function isLicenseTier(value: string): value is LicenseTier {
  return (LICENSE_TIERS as readonly string[]).includes(value);
}

export interface PricePlan {
  tier: LicenseTier;
  /** Höchstzahl Teile; `null` = unbegrenzt. */
  itemLimit: number | null;
  priceGrossCents: number;
  vatPercent: number;
}

/** Buchbare Preisstufen für neue Basare und Upgrades, aufsteigend nach Preis. */
export const PRICE_PLANS: readonly PricePlan[] = [
  { tier: 'free', itemLimit: 100, priceGrossCents: 0, vatPercent: 19 },
  { tier: 'unlimited', itemLimit: null, priceGrossCents: 20_000, vatPercent: 19 },
];

/** Altwerte: nur noch lesbar, nicht mehr buchbar. Preise wie bei ihrer Einführung. */
export const LEGACY_PRICE_PLANS: readonly PricePlan[] = [
  { tier: 'standard', itemLimit: 10_000, priceGrossCents: 20_000, vatPercent: 19 },
  { tier: 'large', itemLimit: 50_000, priceGrossCents: 40_000, vatPercent: 19 },
];

/** Stufen, die die Route für neue Buchungen annimmt (Reihenfolge wie in `PRICE_PLANS`). */
export const BOOKABLE_LICENSE_TIERS = PRICE_PLANS.map((plan) => plan.tier) as [
  LicenseTier,
  ...LicenseTier[],
];

export function isBookableLicenseTier(value: string): value is LicenseTier {
  return (BOOKABLE_LICENSE_TIERS as readonly string[]).includes(value);
}

/** Deutsche Beschriftung je Stufe, für Oberfläche und E-Mail-Vorlagen gleichermaßen. */
export const LICENSE_TIER_LABELS: Readonly<Record<LicenseTier, string>> = {
  free: 'Kostenlos (bis 100 Teile)',
  standard: 'Standard (bis 10.000 Teile)',
  large: 'Groß (bis 50.000 Teile)',
  unlimited: 'Unlimited (unbegrenzt)',
};

/** Plan einer Stufe, auch für Altwerte (bestehende Lizenzen rechnen weiter damit). */
export function planOf(tier: LicenseTier): PricePlan {
  const plan = [...PRICE_PLANS, ...LEGACY_PRICE_PLANS].find((candidate) => candidate.tier === tier);
  if (plan === undefined) throw new Error(`Unbekannte Preisstufe: ${tier}`);
  return plan;
}

/** Kleinste buchbare Stufe, die die erwartete Zahl Teile abdeckt. */
export function tierForItems(expectedItems: number): LicenseTier {
  if (!Number.isInteger(expectedItems) || expectedItems < 0) {
    throw new RangeError('Die erwartete Zahl Teile muss eine ganze Zahl von 0 an sein.');
  }
  for (const plan of PRICE_PLANS) {
    if (plan.itemLimit === null || expectedItems <= plan.itemLimit) return plan.tier;
  }
  // Unerreichbar (die letzte buchbare Stufe hat immer itemLimit null), nur als sicherer Rückfall.
  return 'unlimited';
}

/**
 * Hinweis zur Fälligkeit einer Lizenz. Kostenlose Stufen: kein Hinweis. Kostenpflichtige Stufen: die Zahlung
 * ist erst nach dem Basar fällig (Entscheidung des Auftraggebers, Oktober 2026). Das ist nur eine Anzeige; ob
 * eine Freischaltung vorab nötig bleibt, ist im Code getrennt geregelt (`requiresActiveLicense`).
 */
export function paymentDueNote(priceGrossCents: number): string | null {
  return priceGrossCents > 0 ? 'Zahlung fällig nach Basarende' : null;
}

/** Netto und Umsatzsteuer aus dem Bruttopreis (kaufmännisch gerundet, ganze Cent). */
export function netAndVat(
  grossCents: number,
  vatPercent: number,
): { netCents: number; vatCents: number } {
  const netCents = Math.round((grossCents * 100) / (100 + vatPercent));
  return { netCents, vatCents: grossCents - netCents };
}

/**
 * Downgrade nach der Teile-Grenze, nicht nach dem Preis (Review B3): Eine Altstufe Groß (400 €, 50.000 Teile) auf
 * Unlimited (200 €, ohne Grenze) ist kein Downgrade, sondern ein Wechsel ohne Aufpreis. Ein Downgrade liegt vor, wenn die
 * Zielstufe weniger Teile erlaubt als die aktuelle (oder die aktuelle unbegrenzt ist und die Zielstufe nicht).
 */
export function isLicenseDowngrade(from: LicenseTier, to: LicenseTier): boolean {
  const fromLimit = planOf(from).itemLimit;
  const toLimit = planOf(to).itemLimit;
  if (fromLimit === null) return toLimit !== null;
  return toLimit !== null && toLimit < fromLimit;
}

/** Aufpreis eines Wechsels in Cent (brutto), nie negativ: Ein gleich teurer oder günstigerer Wechsel kostet nichts extra. */
export function surchargeGrossCents(from: LicenseTier, to: LicenseTier): number {
  return Math.max(0, planOf(to).priceGrossCents - planOf(from).priceGrossCents);
}
